import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import './QuestSplash.css'

/**
 * Quest Splash Error
 *
 * "Error should feel like SPLASH (fun, try again!) not BUZZER (wrong, bad, scary)"
 * — Diego Ramirez, Big Foot Pass Philosophy
 *
 * 4-frame animation (500ms total):
 * - Frame 1 (0-100ms): "Oops!" - surprised, ripple begins
 * - Frame 2 (100-250ms): "Splash!" - ripple expands, blue tones
 * - Frame 3 (250-400ms): "Hehe!" - smiling, shaking off water
 * - Frame 4 (400-500ms): "Ready!" - thumbs up, retry options
 *
 * Color palette (SPLASH, not BUZZER):
 * - Primary: #5BA3C0 (warm splash blue)
 * - Secondary: #7BC4E0 (lighter ripple)
 * - Accent: #FFE5B4 (warm highlight)
 * - Text: #2D5A6B (readable but friendly)
 *
 * @param {boolean} isVisible - Whether error is showing
 * @param {string} errorType - 'server' | 'network' | 'input' | 'auth' | 'rate' | 'generic'
 * @param {function} onRetry - Called when user clicks retry
 * @param {function} onDismiss - Called when user dismisses
 */

// Error message translations (Buzzer → Splash)
const ERROR_MESSAGES = {
  server: {
    title: "Whoops! Quest tripped.",
    message: "Let's try that again!",
  },
  network: {
    title: "The internet gremlins are slow today.",
    message: "Keep waiting?",
  },
  input: {
    title: "Hmm, Quest didn't understand that.",
    message: "Try a different way?",
  },
  auth: {
    title: "That password didn't work.",
    message: "Double-check and try again?",
  },
  rate: {
    title: "Quest needs a breather!",
    message: "Try again in a minute?",
  },
  generic: {
    title: "Splash! That didn't work.",
    message: "Let's try again!",
  },
}

const FRAME_TIMINGS = {
  oops: 100,    // 0-100ms
  splash: 150,  // 100-250ms
  hehe: 150,    // 250-400ms
  ready: 100,   // 400-500ms
}

export default function QuestSplash({
  isVisible,
  errorType = 'generic',
  onRetry,
  onDismiss,
}) {
  const [frame, setFrame] = useState('oops')
  const [showRipples, setShowRipples] = useState(false)

  const errorContent = ERROR_MESSAGES[errorType] || ERROR_MESSAGES.generic

  useEffect(() => {
    if (!isVisible) {
      setFrame('oops')
      setShowRipples(false)
      return
    }

    // Frame sequence
    setFrame('oops')
    setShowRipples(true)

    const splashTimer = setTimeout(() => {
      setFrame('splash')
    }, FRAME_TIMINGS.oops)

    const heheTimer = setTimeout(() => {
      setFrame('hehe')
    }, FRAME_TIMINGS.oops + FRAME_TIMINGS.splash)

    const readyTimer = setTimeout(() => {
      setFrame('ready')
      setShowRipples(false)
    }, FRAME_TIMINGS.oops + FRAME_TIMINGS.splash + FRAME_TIMINGS.hehe)

    return () => {
      clearTimeout(splashTimer)
      clearTimeout(heheTimer)
      clearTimeout(readyTimer)
    }
  }, [isVisible])

  const frameConfig = {
    oops: { emoji: '😯', scale: 1.1, message: 'Oops!' },
    splash: { emoji: '💦', scale: 1.0, message: 'Splash!' },
    hehe: { emoji: '😄', scale: 1.05, message: 'Hehe!' },
    ready: { emoji: '👍', scale: 1.0, message: null },
  }

  const config = frameConfig[frame]

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="quest-splash"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
        >
          {/* Ripple effect */}
          {showRipples && (
            <div className="splash-ripples">
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="ripple"
                  initial={{ scale: 0, opacity: 0.8 }}
                  animate={{ scale: 2, opacity: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: i * 0.1,
                    ease: 'easeOut',
                  }}
                />
              ))}
            </div>
          )}

          {/* Quest avatar */}
          <motion.div
            className="splash-avatar"
            animate={{ scale: config.scale }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            <span className="splash-base">🗺️</span>
            <motion.span
              key={config.emoji}
              className="splash-emotion"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.1 }}
            >
              {config.emoji}
            </motion.span>
          </motion.div>

          {/* Error content */}
          <div className="splash-content">
            <AnimatePresence mode="wait">
              {config.message ? (
                <motion.p
                  key={config.message}
                  className="splash-frame-message"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.1 }}
                >
                  {config.message}
                </motion.p>
              ) : (
                <motion.div
                  key="error-content"
                  className="splash-error-content"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <h3 className="splash-title">{errorContent.title}</h3>
                  <p className="splash-message">{errorContent.message}</p>

                  <div className="splash-actions">
                    <motion.button
                      className="splash-btn primary"
                      onClick={onRetry}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Try Again
                    </motion.button>
                    {onDismiss && (
                      <motion.button
                        className="splash-btn secondary"
                        onClick={onDismiss}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Dismiss
                      </motion.button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
